/** Session identity and account actions for the authenticated shell. */
import {
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useAuth } from "../../auth";

function initialsOf(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	const first = parts[0]?.[0] ?? "";
	const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? "" : "";
	return (first + last).toUpperCase();
}

function humanizeRole(role: string | undefined): string | null {
	if (!role) return null;
	return role
		.toLowerCase()
		.split("_")
		.map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
		.join(" ");
}

export function AuthUserChip() {
	const { status, user, activeOrganization, memberships, error } = useAuth();
	const navigate = useNavigate();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	if (status === "loading") {
		return (
			<Stack
				direction="row"
				spacing={1}
				alignItems="center"
				aria-label="Restoring user session"
			>
				<CircularProgress size={20} sx={{ color: "inherit" }} />
			</Stack>
		);
	}

	if (status === "unauthenticated") {
		return (
			<Stack direction="row" spacing={0.5} alignItems="center">
				<Button component={RouterLink} to="/login" color="inherit" size="small">
					Sign in
				</Button>
				<Button
					component={RouterLink}
					to="/register"
					variant="outlined"
					color="inherit"
					size="small"
					sx={{ borderColor: "workspace.borderStrong" }}
				>
					Sign up
				</Button>
			</Stack>
		);
	}

	if (status === "error" || !user) {
		return (
			<Tooltip title={error ? `Session unavailable: ${error.message}` : "No session resolved."}>
				<Chip
					label="Offline"
					size="small"
					variant="outlined"
					sx={{ color: "workspace.textSecondary", borderColor: "workspace.borderStrong" }}
				/>
			</Tooltip>
		);
	}

	const open = Boolean(anchorEl);
	const activeRole = activeOrganization
		? memberships.find(
				(membership) => membership.organizationId === activeOrganization.id
			)?.role
		: undefined;
	const role = humanizeRole(activeRole);

	function close() {
		setAnchorEl(null);
	}

	return (
		<>
			<Stack direction="row" spacing={1} alignItems="center">
				<Box sx={{ textAlign: "right", lineHeight: 1.1, display: { xs: "none", md: "block" } }}>
					<Typography variant="body2" noWrap sx={{ color: "workspace.textPrimary", fontWeight: 650 }}>
						{user.displayName}
					</Typography>
					{role && (
						<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
							{role}
						</Typography>
					)}
				</Box>
				<Tooltip title={`${user.displayName} account menu`}>
					<IconButton
						onClick={(event) => setAnchorEl(event.currentTarget)}
						size="small"
						aria-label={`${user.displayName} account menu`}
						aria-haspopup="menu"
						aria-controls={open ? "account-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Avatar
							sx={{
								width: 34,
								height: 34,
								fontSize: 13,
								fontWeight: 700,
								color: "workspace.background",
								bgcolor: "workspace.green",
								boxShadow: "0 0 0 1px rgba(246,240,227,0.24)",
							}}
						>
							{initialsOf(user.displayName)}
						</Avatar>
					</IconButton>
				</Tooltip>
			</Stack>

			<Menu
				id="account-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={close}
				keepMounted
				MenuListProps={{ "aria-label": "Account actions" }}
			>
				<MenuItem
					onClick={() => {
						close();
						navigate("/account");
					}}
				>
					<ListItemIcon>
						<PersonIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Account" secondary={user.email} />
				</MenuItem>
				<MenuItem
					onClick={() => {
						close();
						navigate("/account?tab=preferences");
					}}
				>
					<ListItemIcon>
						<SettingsIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary="Preferences"
						secondary="Units, locale & appearance"
					/>
				</MenuItem>
				<MenuItem
					onClick={() => {
						close();
						navigate("/logout");
					}}
				>
					<ListItemIcon>
						<LogoutIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary="Sign out" />
				</MenuItem>
			</Menu>
		</>
	);
}
